import {
  Box, Button, HStack, Icon, IconButton, Link as ChakraLink, Tag, Text, VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import {
  FiAlertCircle, FiArrowLeft, FiArrowUpRight, FiSend,
} from "react-icons/fi";
import { FaSlack } from "react-icons/fa";
import { LuCompass } from "react-icons/lu";
import { Link as RouterLink } from "react-router";
import CDS from "design-system";
import ProfileAvatar from "components/ProfileAvatar";
import RichTextAreaField from "components/FormElements/Fields/RichTextAreaField";
import RedirectToSlackMessageOauthForm from "components/Forms/RedirectToSlackMessageOauthForm";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { useCurrentCompanyQuery } from "features/Company/hooks";
import { useCurrentUserQuery } from "features/User/hooks";
import { useIsCurrentUserCompanyAdmin } from "hooks/useCurrentUserPermissions";
import generateReminderBodyForTasks, { REMINDER_TEMPLATES, ReminderTemplate } from "features/SendMessage/utils/generateReminderBody";
import { format, formatDistanceToNow } from "date-fns";
import { ReminderRow } from "../types";
import SenderToggle, { SenderType } from "./SenderToggle";
import { useSendMessage } from "../hooks";

const LABEL_W = "20";

// Strips trix-editor's default border so it sits flush inside the compose box.
const BORDERLESS_TRIX_SX = {
  ".trix-container trix-editor": {
    border: "none !important",
    borderRadius: "0 !important",
    padding: "0 !important",
    minHeight: "80px",
  },
  ".trix-container": {
    gap: "8px !important",
  },
  ".trix-button--icon-attach, .trix-button--icon-decrease-nesting-level, .trix-button--icon-increase-nesting-level, .trix-button--icon-undo, .trix-button--icon-redo": {
    display: "none !important",
  },
};

interface ReminderComposerProps {
  row: ReminderRow;
  newHireFullName?: string;
  newHireId?: string;
  onBack: () => void;
  onSent: () => void;
}

export default function ReminderComposer({
  row, newHireFullName, newHireId, onBack, onSent,
}: ReminderComposerProps) {
  const { data: company } = useCurrentCompanyQuery();
  const { data: currentUser } = useCurrentUserQuery({ staleTime: 0 });
  const isCompanyAdmin = useIsCurrentUserCompanyAdmin();

  const isAuthorized = currentUser?.sendMessageAuthorized ?? false;
  const [senderType, setSenderType] = useState<SenderType>(isAuthorized ? "me" : "bot");
  const [isSending, setIsSending] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<ReminderTemplate>("friendly");

  // Track whether currentUser has loaded at least once. Before that, isAuthorized
  // is false by default and would prematurely switch an authorized user to "bot".
  // After the first load we leave senderType alone so that a mid-session token
  // expiry surfaces the reauth form instead of silently switching to bot.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!currentUser) return;
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (!isAuthorized) setSenderType("bot");
    }
  }, [currentUser, isAuthorized]);

  const caminoUrl = useMemo(() => `${window.location.origin}/my-tasks`, []);
  const slackUrl = company?.messageService?.deepLinkUrl;
  // Surface is company-wide policy, not per-message. Fall back to Camino if Slack isn't connected,
  // so messages never include a dead slack:// link.
  const wantsSlack = company?.taskResolutionSurface === "slack_home";
  const usingSlack = wantsSlack && !!slackUrl;
  const currentUrl = usingSlack ? slackUrl : caminoUrl;

  const baselineFor = useCallback(
    (template: ReminderTemplate) =>
      generateReminderBodyForTasks(row.user, row.tasks, currentUrl, template, newHireFullName, newHireId),
    [row.user, row.tasks, currentUrl, newHireFullName, newHireId],
  );

  const draftMessage = useMemo(() => baselineFor("friendly"), [baselineFor]);

  const { control, watch, setValue } = useForm<FieldValues>({
    defaultValues: { message: draftMessage },
  });

  const messageValue = watch("message");

  const draftsRef = useRef<Partial<Record<ReminderTemplate, string>>>({});
  const editorRef = useRef<any>(null);

  const handleTemplateChange = useCallback((next: ReminderTemplate) => {
    if (next === activeTemplate) return;
    draftsRef.current[activeTemplate] = messageValue;
    const nextHtml = draftsRef.current[next] ?? baselineFor(next);
    setActiveTemplate(next);
    setValue("message", nextHtml, { shouldDirty: true });
    editorRef.current?.loadHTML(nextHtml);
  }, [activeTemplate, messageValue, baselineFor, setValue]);

  const { mutate: sendMessage } = useSendMessage({
    transformData: (data) => ({
      recipientType: "User",
      recipientId: row.user.id,
      message: data.message,
      taskIds: row.tasks.map((t) => t.id),
      sendAsBot: senderType === "bot",
    }),
    onSuccess: () => {
      setIsSending(false);
      onSent();
    },
    onError: () => {
      setIsSending(false);
    },
  });

  const handleSend = useCallback(() => {
    setIsSending(true);
    sendMessage({ message: messageValue });
  }, [messageValue, sendMessage]);

  const openCount = row.overdueCount + row.urgentCount + row.activeCount;
  const activeTemplateConfig = REMINDER_TEMPLATES.find((t) => t.value === activeTemplate);

  return (
    <VStack align="stretch" spacing={5}>
      {/* Header */}
      <HStack spacing={3} align="center">
        <IconButton
          aria-label="Back to list"
          icon={<Icon as={FiArrowLeft} />}
          variant="ghost"
          size="sm"
          onClick={onBack}
        />
        <VStack align="start" spacing={0} flex={1}>
          <CDS.Heading18 color="fg.default">
            Remind
            {" "}
            {row.user.firstName}
          </CDS.Heading18>
          <Text fontSize="sm" color="fg.muted">
            {openCount}
            {" "}
            {openCount === 1 ? "task" : "tasks"}
            {row.overdueCount > 0 && (
              <>
                {" · "}
                <Text as="span" color="red.600" fontWeight="medium">overdue</Text>
              </>
            )}
            {newHireId && row.user.id === newHireId
              ? " · for your onboarding"
              : newHireFullName && ` · for ${newHireFullName}'s onboarding`}
          </Text>
        </VStack>
      </HStack>

      {/* Recently-reminded warning */}
      {row.recency === "recent" && row.lastReminderAt && (
        <HStack
          align="flex-start"
          spacing={3}
          p={3}
          bg="orange.50"
          borderWidth={1}
          borderColor="orange.200"
          borderRadius="md"
        >
          <Icon as={FiAlertCircle} color="orange.500" boxSize={4} mt={0.5} aria-hidden />
          <Text fontSize="sm" color="orange.800">
            <Text as="span" fontWeight="medium">{row.user.firstName}</Text>
            {" "}
            was reminded
            {" "}
            <Text as="span" title={format(new Date(row.lastReminderAt), "MMM d, yyyy 'at' h:mm a")}>
              {formatDistanceToNow(new Date(row.lastReminderAt), { addSuffix: true })}
            </Text>
            . Sending another so soon may read as nagging.
          </Text>
        </HStack>
      )}

      {!isAuthorized && senderType === "me" ? (
        <RedirectToSlackMessageOauthForm />
      ) : (
        <>
          {/* Send as */}
          <HStack spacing={3} align="center">
            <Text fontSize="sm" fontWeight="medium" color="fg.muted" w={LABEL_W} flexShrink={0}>
              Send as
            </Text>
            <SenderToggle
              value={senderType}
              onChange={setSenderType}
              isAuthorized={isAuthorized}
            />
          </HStack>

          {/* Resolve in — surface is a company-wide setting; reminder itself always goes via Slack */}
          <Box>
            <Text fontSize="sm" color="fg.muted" lineHeight="tall">
              The &ldquo;your tasks&rdquo; link sends them to
              {" "}
              <Tag
                size="sm"
                variant="subtle"
                colorScheme="gray"
                px={2}
                py={0.5}
                borderRadius="md"
                verticalAlign="middle"
              >
                <Icon as={usingSlack ? FaSlack : LuCompass} boxSize={3} mr={1} />
                <Text fontSize="xs" fontWeight="medium">
                  {usingSlack ? "Slack Camino App Home Tab" : "Camino app · My Tasks page"}
                </Text>
              </Tag>
              .
              {isCompanyAdmin && (
                <>
                  {" "}
                  <ChakraLink
                    as={RouterLink}
                    to={PEOPLE_TEAM_ROUTES.company.taskSettings}
                    color="cdsIndigo.600"
                    display="inline-flex"
                    alignItems="center"
                    gap={0.5}
                    aria-label="Change resolution surface in Task Settings"
                  >
                    Change in Settings
                    <Icon as={FiArrowUpRight} boxSize={3} />
                  </ChakraLink>
                </>
              )}
              {wantsSlack && !slackUrl && (
                <Text as="span" color="orange.600" ml={2}>
                  · Slack workspace isn&apos;t connected, so the button will fall back to Camino.
                  {isCompanyAdmin && (
                    <>
                      {" "}
                      <ChakraLink
                        as={RouterLink}
                        to={PEOPLE_TEAM_ROUTES.company.integrations.index}
                        color="cdsIndigo.600"
                        display="inline-flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        Connect Slack
                        <Icon as={FiArrowUpRight} boxSize={3} />
                      </ChakraLink>
                    </>
                  )}
                </Text>
              )}
            </Text>
          </Box>

          {/* Tone */}
          <HStack spacing={3} align="flex-start">
            <Text fontSize="sm" fontWeight="medium" color="fg.muted" w={LABEL_W} flexShrink={0} pt={1}>
              Tone
            </Text>
            <VStack align="start" spacing={1.5}>
              <HStack spacing={2} flexWrap="wrap">
                {REMINDER_TEMPLATES.map((t) => (
                  <Button
                    key={t.value}
                    size="sm"
                    h={8}
                    px={4}
                    borderRadius="full"
                    variant={activeTemplate === t.value ? "solid" : "outline"}
                    colorScheme={activeTemplate === t.value ? "cdsIndigo" : "gray"}
                    onClick={() => handleTemplateChange(t.value)}
                    fontWeight="medium"
                  >
                    {t.label}
                  </Button>
                ))}
              </HStack>
              {activeTemplateConfig && (
                <Text fontSize="xs" color="fg.muted">
                  {activeTemplateConfig.description}
                </Text>
              )}
            </VStack>
          </HStack>

          {/* Compose box — combines "To" header + sender context + editable message */}
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="xl"
            overflow="hidden"
          >
            {/* To header */}
            <HStack px={4} py={2.5} borderBottom="1px solid" borderColor="gray.100" bg="gray.50" spacing={2}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">To</Text>
              <ProfileAvatar user={row.user} size="xs" />
              <Text fontSize="sm" fontWeight="semibold" flex={1} color="fg.default">{row.user.fullName}</Text>
              <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                {senderType === "bot" ? "Message from Camino App · Slack" : "Direct message · Slack"}
              </Text>
            </HStack>

            {/* Editable message */}
            <Box px={4} pt={3} pb={0} sx={BORDERLESS_TRIX_SX}>
              <RichTextAreaField
                name="message"
                hideToolbar={false}
                alwaysShowToolbar
                control={control}
                errors={{}}
                onInitialize={(event: Event) => {
                  editorRef.current = (event.target as any).editor;
                }}
              />
            </Box>
          </Box>

          {/* Footer */}
          <HStack justify="flex-end" align="center" pt={1}>
            <Button
              colorScheme="cdsIndigo"
              size="md"
              leftIcon={<Icon as={FiSend} />}
              onClick={handleSend}
              isLoading={isSending}
              loadingText="Sending..."
            >
              Send Reminder
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
}
